import React, { useEffect, useState } from 'react';
import {
  CssBaseline, Box, Typography, Button, TextField, Table, TableBody, TableCell,
  tableCellClasses, TableContainer, TableHead, TableRow, Paper, CircularProgress,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, Select, MenuItem, Autocomplete,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CelebrationIcon from '@mui/icons-material/Celebration';
import PublicIcon from '@mui/icons-material/Public';
import Header from '../Includes/Header';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import AppToast from '../../components/Toast/AppToast';
import { getHolidays, addHoliday, updateHoliday, deleteHoliday } from '../Apihelper/HolidayApi';
import Holidays from 'date-holidays';
import dayjs from 'dayjs';

const BG = '#F4F7F4';

// The backend returns holiday dates as "DD-MM-YYYY", but a freshly-typed date
// (or one built from date-holidays) may still be "YYYY-MM-DD" — parse both
// safely instead of relying on the native Date constructor's ambiguous
// dash-separated parsing (which silently mis-parses or fails).
const parseHolidayDate = (value) => {
  if (!value) return null;
  const ddmmyyyy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
  if (ddmmyyyy) {
    const [, d, m, y] = ddmmyyyy;
    return dayjs(`${y}-${m}-${d}`);
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 'bold',
    borderBottom: 'none',
  },
  [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));

const StyledTableRow = styled(TableRow)(() => ({
  '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
  '&:last-child td, &:last-child th': { border: 0 },
}));

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => currentYear - 1 + i);

// Common Odia festivals / regional holidays — just a name shortlist to speed
// up entry (freeSolo, so any custom name can still be typed). Dates aren't
// prefilled since most of these shift every year on the lunar calendar.
const ODIA_FESTIVAL_NAMES = [
  'Makar Sankranti', 'Vasant Panchami', 'Maha Shivaratri', 'Ashokastami',
  'Bahuda Yatra', 'Rath Yatra', 'Raja Sankranti (Mithuna Sankranti)',
  'Nuakhai', 'Ganesh Chaturthi', 'Kumar Purnima', 'Prathamastami',
  'Durga Puja', 'Lakshmi Puja', 'Kartik Purnima', 'Bali Yatra',
  'Diwali', 'Kali Puja', 'Bhai Jiuntia', 'Dola Purnima (Dola Yatra)',
  'Holi', 'Chhena Poda Day', 'Utkal Divas (Odisha Day)',
  'Independence Day', 'Republic Day', 'Gandhi Jayanti', 'Christmas',
];

// Company-wide paid holidays (Odia festivals, national holidays, etc.) — a day
// listed here is treated exactly like a Sunday in Salary Generation: excluded
// from working-day totals and never counted as absent/half-day, regardless of
// attendance. Admin adds each year's dates manually since festival dates
// shift every year on the lunar calendar.
const HolidayCalendar = () => {
  const { colors: appColors } = useAppTheme();
  const [token, setToken] = useState('');
  const [year, setYear] = useState(currentYear);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formDate, setFormDate] = useState(''); // "YYYY-MM-DD" string (native date input)
  const [formName, setFormName] = useState('');
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  const [openToast, setOpenToast] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastSeverity, setToastSeverity] = useState('success');
  const showToast = (msg, sev = 'success') => {
    setToastMsg(msg);
    setToastSeverity(sev);
    setOpenToast(true);
  };

  useEffect(() => {
    const userData = localStorage.getItem('userdata');
    if (userData) setToken(JSON.parse(userData).token);
  }, []);

  const loadHolidays = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await getHolidays(token, year);
      setHolidays(res.code === 200 && Array.isArray(res.data) ? res.data : []);
    } catch {
      showToast('Failed to load holidays.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHolidays(); }, [token, year]);

  const openAddDialog = () => {
    setEditingId(null);
    setFormDate('');
    setFormName('');
    setDialogOpen(true);
  };

  const openEditDialog = (holiday) => {
    setEditingId(holiday.id);
    setFormDate(parseHolidayDate(holiday.date)?.format('YYYY-MM-DD') || '');
    setFormName(holiday.name);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formDate || !formName.trim()) {
      showToast('Please enter both a date and a name.', 'warning');
      return;
    }
    setSaving(true);
    try {
      const res = editingId
        ? await updateHoliday(token, editingId, formDate, formName.trim())
        : await addHoliday(token, formDate, formName.trim());

      if (res.code === 200) {
        showToast(res.message || 'Saved.');
        setDialogOpen(false);
        loadHolidays();
      } else {
        showToast(typeof res.message === 'string' ? res.message : 'Failed to save holiday.', 'error');
      }
    } catch {
      showToast('Failed to save holiday.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await deleteHoliday(token, id);
      if (res.code === 200) {
        showToast('Holiday deleted.');
        loadHolidays();
      } else {
        showToast(res.message || 'Failed to delete holiday.', 'error');
      }
    } catch {
      showToast('Failed to delete holiday.', 'error');
    }
  };

  const handleImportNationalHolidays = async () => {
    setImporting(true);
    try {
      const hd = new Holidays('IN');
      const yearHolidays = hd.getHolidays(year).filter((h) => h.type === 'public');
      const existingDates = new Set(holidays.map((h) => h.date));

      let added = 0, skipped = 0, failed = 0;
      for (const h of yearHolidays) {
        const date = h.date.slice(0, 10); // "YYYY-MM-DD hh:mm:ss" -> "YYYY-MM-DD"
        if (existingDates.has(date)) { skipped++; continue; }
        try {
          const res = await addHoliday(token, date, h.name);
          if (res.code === 200) added++; else failed++;
        } catch {
          failed++;
        }
      }

      showToast(
        `Imported ${added} national holiday${added === 1 ? '' : 's'} for ${year}` +
          (skipped ? `, ${skipped} already listed` : '') +
          (failed ? `, ${failed} failed` : '') + '.',
        failed ? 'warning' : 'success'
      );
      loadHolidays();
    } catch {
      showToast('Failed to import national holidays.', 'error');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, minHeight: '100vh', backgroundColor: BG }}>
      <CssBaseline />
      <Header />

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, overflowX: 'hidden', overflowY: 'auto', p: { xs: '12px', sm: '20px', md: '24px' }, mt: { xs: '56px', sm: '64px' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CelebrationIcon sx={{ color: appColors.leaf, fontSize: 28 }} />
            <Typography sx={{ fontSize: { xs: '20px', sm: '24px' }, fontWeight: 800, color: appColors.leaf }}>
              Holiday Calendar
            </Typography>
          </Box>
          {/* Phone: year and Add Holiday share the first line, Import takes the full second line */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'auto auto auto' }, gap: 1.5, alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
            <FormControl size="small" sx={{ order: 1 }}>
              <Select value={year} onChange={(e) => setYear(e.target.value)} sx={{ minWidth: 100 }}>
                {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
            <Tooltip title={`Import India's national public holidays for ${year}`}>
              <Box component="span" sx={{ display: 'block', order: { xs: 3, sm: 2 }, gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={importing ? <CircularProgress size={16} /> : <PublicIcon />}
                  onClick={handleImportNationalHolidays}
                  disabled={importing || loading}
                  sx={{ whiteSpace: 'nowrap', borderColor: appColors.leaf, color: appColors.leaf, '&:hover': { borderColor: appColors.pine, bgcolor: 'transparent' } }}
                >
                  Import National Holidays
                </Button>
              </Box>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAddDialog}
              sx={{ order: { xs: 2, sm: 3 }, whiteSpace: 'nowrap', bgcolor: appColors.leaf, '&:hover': { bgcolor: appColors.pine } }}
            >
              Add Holiday
            </Button>
          </Box>
        </Box>

        <Typography sx={{ fontSize: '0.85rem', color: '#64748b', mb: 2.5 }}>
          Dates listed here are treated as a paid day off in Salary Generation — excluded from working days,
          never counted absent, regardless of attendance. Add each year's Odia festival/holiday dates here.
        </Typography>

        <TableContainer component={Paper} sx={{ borderRadius: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.12)', overflowX: 'auto' }}>
          {loading ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <CircularProgress sx={{ color: appColors.leaf }} />
            </Box>
          ) : (
            <Table sx={{ minWidth: 520 }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell>S.NO</StyledTableCell>
                  <StyledTableCell>DATE</StyledTableCell>
                  <StyledTableCell>DAY</StyledTableCell>
                  <StyledTableCell>HOLIDAY NAME</StyledTableCell>
                  <StyledTableCell align="right">ACTIONS</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {holidays.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4, color: '#94a3b8' }}>
                      No holidays added for {year} yet.
                    </TableCell>
                  </TableRow>
                ) : holidays.map((h, i) => (
                  <StyledTableRow key={h.id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{h.date}</TableCell>
                    <TableCell>{parseHolidayDate(h.date)?.format('dddd') || '—'}</TableCell>
                    <TableCell>{h.name}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEditDialog(h)} sx={{ color: appColors.leaf }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(h.id)} sx={{ color: '#dc2626' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingId ? 'Edit Holiday' : 'Add Holiday'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField
            label="Date"
            type="date"
            value={formDate}
            onChange={(e) => setFormDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <Autocomplete
            freeSolo
            options={ODIA_FESTIVAL_NAMES}
            value={formName}
            onInputChange={(event, newValue) => setFormName(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Holiday Name"
                placeholder="e.g. Rath Yatra"
                fullWidth
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ bgcolor: appColors.leaf, '&:hover': { bgcolor: appColors.pine } }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <AppToast open={openToast} message={toastMsg} severity={toastSeverity} onClose={() => setOpenToast(false)} />
    </Box>
  );
};

export default HolidayCalendar;
